import { useEffect, useMemo, useState } from 'react';
import ConfirmBar from './ConfirmBar';
import HealthFinalizedView from './HealthFinalizedView';
import { useT } from '../context/LocaleContext';
import { resolveMediaObject } from '../lib/bookingMedia';
import {
  SCORE_FIELDS,
  applyScoreOverrides,
  computeHealthScores,
  scoreBand,
} from '../lib/healthScore';
import { vehicleTitle } from '../lib/format';
import { supabase } from '../lib/supabase';

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good', hint: 'No issues found' },
  { value: 'fair', label: 'Fair', hint: 'Monitor / service soon' },
  { value: 'poor', label: 'Poor', hint: 'Needs attention' },
];

const SERVICE_SUGGESTIONS = [
  'Oil & filter',
  'Brake pads',
  'Brake fluid',
  'Tire rotation',
  'Wheel alignment',
  'Battery test',
  'Coolant flush',
  'Air filter',
  'Spark plugs',
  'Transmission service',
  'AC service',
  'Suspension check',
];

const DIAGNOSTIC_SUGGESTIONS = [
  'No warning lights',
  'Check engine light',
  'ABS warning',
  'Battery / charge light',
  'Oil pressure warning',
  'Coolant temperature warning',
  'TPMS / tire pressure',
  'Airbag warning',
  'Unusual engine noise',
  'Vibration while driving',
];

const ACCIDENT_SUGGESTIONS = [
  'No accident history reported',
  'Previous body repair',
  'Front bumper repair',
  'Rear bumper repair',
  'Side impact repair',
  'Windshield replaced',
  'Underbody damage',
  'Suspension impact damage',
];

const SCORE_LABELS = {
  overall_score: 'Overall',
  engine_score: 'Engine',
  braking_score: 'Braking',
  tires_score: 'Tires',
  battery_score: 'Battery',
  cooling_score: 'Cooling',
  suspension_score: 'Suspension',
  electrical_score: 'Electrical',
};

function emptyService() {
  return { key: crypto.randomUUID(), service: '', notes: '' };
}

function servicesFromReport(rows) {
  if (!rows?.length) return [emptyService()];
  return rows.map((row, index) => ({
    key: row.id || `${index}`,
    service: row.service || '',
    notes: row.notes || '',
  }));
}

/** Drop Diagnostics / Accident blocks that older finalize() copies mixed into vehicle notes. */
function pureMaintenanceNotes(text) {
  if (!text?.trim()) return '';
  const out = [];
  let skippingLabeled = false;
  for (const line of String(text).split('\n')) {
    const trimmed = line.trim();
    const lower = trimmed.toLowerCase();
    if (
      lower.startsWith('diagnostics:') ||
      lower.startsWith('accident / repairs:')
    ) {
      skippingLabeled = true;
      continue;
    }
    if (skippingLabeled) {
      if (!trimmed) skippingLabeled = false;
      continue;
    }
    out.push(line);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function formFromReport(report, vehicle) {
  const savedMileage = report?.mileage;
  const vehicleMileage = vehicle?.mileage;
  const reportMaint = report?.maintenance_history_notes;
  const vehicleMaint = vehicle?.previous_maintenance_notes;
  return {
    mileage:
      savedMileage && savedMileage > 0
        ? String(savedMileage)
        : vehicleMileage
          ? String(vehicleMileage)
          : '',
    services: servicesFromReport(report?.overdue_services),
    diagnosticFindings: report?.diagnostic_findings || '',
    accidentRepairNotes: report?.accident_repair_notes || '',
    photos: Array.isArray(report?.inspection_photos) ? report.inspection_photos : [],
    tireCondition: report?.tire_condition || '',
    batteryCondition: report?.battery_condition || '',
    componentAges: report?.component_ages || {},
    maintenanceHistoryNotes: pureMaintenanceNotes(
      reportMaint || vehicleMaint || '',
    ),
  };
}

function reportPayload(form, scores, overrides) {
  return {
    mileage: Number(form.mileage) || 0,
    overdue_services: form.services
      .filter((row) => row.service.trim())
      .map((row) => ({ service: row.service.trim(), notes: row.notes?.trim() || null })),
    diagnostic_findings: form.diagnosticFindings.trim(),
    accident_repair_notes: form.accidentRepairNotes.trim(),
    inspection_photos: form.photos,
    tire_condition: form.tireCondition || null,
    battery_condition: form.batteryCondition || null,
    component_ages: form.componentAges,
    maintenance_history_notes: form.maintenanceHistoryNotes.trim() || null,
    overall_score: scores.overall_score,
    engine_score: scores.engine_score,
    braking_score: scores.braking_score,
    tires_score: scores.tires_score,
    battery_score: scores.battery_score,
    cooling_score: scores.cooling_score,
    suspension_score: scores.suspension_score,
    electrical_score: scores.electrical_score,
    score_overrides: overrides,
  };
}

function scoresFromReport(report) {
  return {
    overall_score: report?.overall_score ?? 100,
    engine_score: report?.engine_score ?? 100,
    braking_score: report?.braking_score ?? 100,
    tires_score: report?.tires_score ?? 100,
    battery_score: report?.battery_score ?? 100,
    cooling_score: report?.cooling_score ?? 100,
    suspension_score: report?.suspension_score ?? 100,
    electrical_score: report?.electrical_score ?? 100,
  };
}

function appendSuggestion(current, suggestion) {
  const value = (current || '').trim();
  if (!value) return suggestion;
  if (value.toLowerCase().includes(suggestion.toLowerCase())) return value;
  return `${value}\n${suggestion}`;
}

function HealthScoreSlider({ value, onChange, ariaLabel }) {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <input
      className="health-slider"
      type="range"
      min="0"
      max="100"
      step="1"
      value={safe}
      aria-label={ariaLabel}
      style={{ '--progress': `${safe}%` }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ChipRow({ label, options, onPick, disabled }) {
  if (disabled) return null;
  return (
    <div className="health-chip-row">
      {label ? <span className="health-chip-label">{label}</span> : null}
      <div className="health-chips">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className="health-chip"
            onClick={() => onPick(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function ConditionPicker({ value, onChange, disabled }) {
  const t = useT();
  return (
    <div className="health-condition-grid">
      {CONDITION_OPTIONS.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={`health-condition-card${active ? ' active' : ''}`}
            disabled={disabled}
            onClick={() => onChange(active ? '' : option.value)}
          >
            <strong>{t(`health.${option.value}`)}</strong>
            <span>{t(`health.${option.value}Hint`)}</span>
          </button>
        );
      })}
    </div>
  );
}

function HealthPhoto({ item }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    let revoke = false;
    async function load() {
      const resolved = await resolveMediaObject(item);
      if (!alive) return;
      revoke = resolved.revoke;
      if (resolved.error) {
        setError(resolved.error);
        return;
      }
      setUrl(resolved.url);
    }
    load();
    return () => {
      alive = false;
      if (revoke && url) URL.revokeObjectURL(url);
    };
  }, [item]);

  if (error) return <div className="health-photo health-photo-error">{error}</div>;
  if (!url) return <div className="health-photo health-photo-loading">Loading…</div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="health-photo">
      <img src={url} alt="" />
    </a>
  );
}

export default function HealthInspectionPanel({ booking, onFinalized }) {
  const t = useT();
  const vehicle = booking.vehicles || {};
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(() => formFromReport(null, vehicle));
  const [draftScores, setDraftScores] = useState(scoresFromReport(null));
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const canManageHealth =
    booking.status === 'checked_in' ||
    booking.status === 'in_progress' ||
    booking.status === 'completed';
  const finalized = report?.status === 'finalized';
  const readOnly = finalized || !canManageHealth;
  const savedMileage = vehicle.mileage;

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      const { data, error: queryError } = await supabase
        .from('vehicle_health_reports')
        .select('*')
        .eq('booking_id', booking.id)
        .maybeSingle();
      if (!alive) return;
      if (queryError) {
        setError(queryError.message);
        setLoading(false);
        return;
      }
      const currentVehicle = booking.vehicles || {};
      setReport(data);
      setForm(formFromReport(data, currentVehicle));
      setDraftScores(scoresFromReport(data));
      setOverrides(data?.score_overrides || {});
      setLoading(false);
    }
    load();
    return () => {
      alive = false;
    };
  }, [
    booking.id,
    booking.vehicle_id,
    booking.vehicles?.mileage,
    booking.vehicles?.previous_maintenance_notes,
  ]);

  const computed = useMemo(
    () =>
      computeHealthScores({
        overdue_services: form.services
          .filter((row) => row.service.trim())
          .map((row) => ({ service: row.service.trim(), notes: row.notes })),
        diagnostic_findings: form.diagnosticFindings,
        accident_repair_notes: form.accidentRepairNotes,
        maintenance_history_notes: form.maintenanceHistoryNotes,
        tire_condition: form.tireCondition || null,
        battery_condition: form.batteryCondition || null,
      }),
    [form],
  );

  const displayScores = useMemo(
    () => applyScoreOverrides(computed, draftScores, overrides),
    [computed, draftScores, overrides],
  );

  const requiredReady = useMemo(() => {
    const hasMileage = Number(form.mileage) > 0;
    const hasServices = form.services.some((row) => row.service.trim());
    const hasDiagnostics = form.diagnosticFindings.trim().length > 0;
    const hasAccident = form.accidentRepairNotes.trim().length > 0;
    const hasPhotos = form.photos.length > 0;
    return { hasMileage, hasServices, hasDiagnostics, hasAccident, hasPhotos };
  }, [form]);

  const readyCount = Object.values(requiredReady).filter(Boolean).length;

  function setService(key, patch) {
    setForm((prev) => ({
      ...prev,
      services: prev.services.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    }));
  }

  function addSuggestedService(name) {
    setForm((prev) => {
      const exists = prev.services.some(
        (row) => row.service.trim().toLowerCase() === name.toLowerCase(),
      );
      if (exists) return prev;
      const blank = prev.services.find((row) => !row.service.trim());
      if (blank) {
        return {
          ...prev,
          services: prev.services.map((row) =>
            row.key === blank.key ? { ...row, service: name } : row,
          ),
        };
      }
      return {
        ...prev,
        services: [...prev.services, { key: crypto.randomUUID(), service: name, notes: '' }],
      };
    });
  }

  function setScore(field, value) {
    const next = Number(value);
    setDraftScores((prev) => ({ ...prev, [field]: next }));
    setOverrides((prev) => ({ ...prev, [field]: true }));
  }

  function resetScore(field) {
    setOverrides((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    setDraftScores((prev) => ({ ...prev, [field]: computed[field] }));
  }

  async function persistDraft() {
    setSaving(true);
    setError('');
    setOk('');
    const payload = reportPayload(form, displayScores, overrides);
    const base = {
      booking_id: booking.id,
      vehicle_id: booking.vehicle_id,
      branch_id: booking.branch_id,
      ...payload,
      status: 'draft',
      updated_at: new Date().toISOString(),
    };

    let saved;
    if (report?.id) {
      const { data, error: saveError } = await supabase
        .from('vehicle_health_reports')
        .update(base)
        .eq('id', report.id)
        .select('*')
        .single();
      if (saveError) {
        setSaving(false);
        setError(saveError.message);
        return null;
      }
      saved = data;
    } else {
      const { data, error: saveError } = await supabase
        .from('vehicle_health_reports')
        .insert({ ...base, created_by: (await supabase.auth.getUser()).data.user?.id })
        .select('*')
        .single();
      if (saveError) {
        setSaving(false);
        setError(saveError.message);
        return null;
      }
      saved = data;
    }

    setReport(saved);
    setSaving(false);
    setOk('Draft saved.');
    return saved;
  }

  async function uploadPhotos(fileList) {
    if (!fileList?.length) return;
    setUploading(true);
    setError('');
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData?.user?.id) {
      setUploading(false);
      setError(authError?.message || 'Sign in again to upload photos.');
      return;
    }
    // Root folder must be auth.uid() or the staff branch id (storage_path_allowed).
    // Prefer the signed-in user id so managers without a branch can still upload.
    const rootFolder = authData.user.id;
    const reportId = report?.id || crypto.randomUUID();
    const uploaded = [];
    for (const file of fileList) {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${rootFolder}/health/${booking.vehicle_id}/${reportId}/${crypto.randomUUID()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('vehicle-photos')
        .upload(path, file, { upsert: false });
      if (uploadError) {
        setUploading(false);
        setError(uploadError.message);
        return;
      }
      uploaded.push({ bucket: 'vehicle-photos', path, name: file.name });
    }
    setForm((prev) => ({ ...prev, photos: [...prev.photos, ...uploaded] }));
    setUploading(false);
  }

  async function reopenReport() {
    if (!report?.id) return;
    setSaving(true);
    setError('');
    setOk('');
    const { data, error: reopenError } = await supabase.rpc(
      'reopen_vehicle_health_report',
      { p_report_id: report.id },
    );
    setSaving(false);
    if (reopenError) {
      setError(reopenError.message);
      return;
    }
    setReport(data);
    setForm(formFromReport(data, vehicle));
    setDraftScores(scoresFromReport(data));
    setOverrides(data?.score_overrides || {});
    setOk('Report reopened for editing. Save and finalize when done.');
  }

  async function finalizeReport() {
    const saved = await persistDraft();
    if (!saved) return;
    setSaving(true);
    setError('');
    const { data, error: finalizeError } = await supabase.rpc(
      'finalize_vehicle_health_report',
      { p_report_id: saved.id },
    );
    setSaving(false);
    if (finalizeError) {
      setError(finalizeError.message);
      return;
    }
    setReport(data);
    setDraftScores(scoresFromReport(data));
    setConfirmFinalize(false);
    setOk(t('health.finalized'));
    onFinalized?.(data);
  }

  if (loading) return <p className="empty">{t('health.loading')}</p>;

  if (!canManageHealth && !report) {
    return (
      <p className="empty">
        {t('health.afterCheckin')}
      </p>
    );
  }

  return (
    <article className="quote-sheet health-sheet">
      <header className="quote-head">
        <span className="material-symbol">health_and_safety</span>
        <div>
          <h2>{t('health.title')}</h2>
          <p>
            {finalized ? t('health.ledeDone') : t('health.ledeOpen')}
          </p>
        </div>
      </header>

      {finalized ? null : (
      <div className="health-vehicle-card">
        <div>
          <span className="kicker">Inspecting</span>
          <strong>{vehicleTitle(vehicle)}</strong>
          <p>
            {[vehicle.plate_number, vehicle.vin ? `VIN ${vehicle.vin}` : null]
              .filter(Boolean)
              .join(' · ') || 'No plate / VIN on file'}
          </p>
        </div>
        <div className="health-vehicle-meta">
          <span>{t('health.savedMileage')}</span>
          <strong>
            {savedMileage != null
              ? `${Number(savedMileage).toLocaleString()} km`
              : t('health.notSet')}
          </strong>
        </div>
      </div>
      )}

      {error && <p className="form-error">{error}</p>}
      {ok && <p className="form-ok">{ok}</p>}

      {finalized ? (
        <HealthFinalizedView
          report={report}
          vehicle={vehicle}
          booking={booking}
          busy={saving}
          onEdit={reopenReport}
        />
      ) : (
        <>
          <div className="health-progress">
            <div className="health-progress-bar">
              <span style={{ width: `${(readyCount / 5) * 100}%` }} />
            </div>
            <p>{t('health.progress', { n: readyCount })}</p>
          </div>

          <form
            className="quote-compose health-compose"
            onSubmit={(e) => {
              e.preventDefault();
              persistDraft();
            }}
          >
        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>1</em> {t('health.mileage')}
            </h3>
            <span className={requiredReady.hasMileage ? 'ok' : 'need'}>
              {requiredReady.hasMileage ? t('health.ready') : t('health.required')}
            </span>
          </div>
          <p className="health-help">
            {t('health.mileageHelp')}
          </p>
          <div className="health-mileage-row">
            <label>
              {t('health.odometer')}
              <input
                type="number"
                min="1"
                required
                disabled={readOnly}
                value={form.mileage}
                onChange={(e) => setForm((prev) => ({ ...prev, mileage: e.target.value }))}
              />
            </label>
            {!readOnly && savedMileage != null ? (
              <button
                type="button"
                className="ghost"
                onClick={() =>
                  setForm((prev) => ({ ...prev, mileage: String(savedMileage) }))
                }
              >
                Use saved {Number(savedMileage).toLocaleString()} km
              </button>
            ) : null}
          </div>
        </section>

        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>2</em> {t('health.services')}
            </h3>
            <span className={requiredReady.hasServices ? 'ok' : 'need'}>
              {requiredReady.hasServices ? t('health.ready') : t('health.required')}
            </span>
          </div>
          <p className="health-help">
            Tap a suggestion to add it, then add notes if needed.
          </p>
          <ChipRow
            options={SERVICE_SUGGESTIONS}
            disabled={readOnly}
            onPick={addSuggestedService}
          />
          {form.services.map((row) => (
            <div className="health-service-row" key={row.key}>
              <input
                placeholder="Service name"
                disabled={readOnly}
                value={row.service}
                onChange={(e) => setService(row.key, { service: e.target.value })}
              />
              <input
                placeholder="Notes (optional)"
                disabled={readOnly}
                value={row.notes}
                onChange={(e) => setService(row.key, { notes: e.target.value })}
              />
              {!readOnly && form.services.length > 1 ? (
                <button
                  type="button"
                  className="ghost"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      services: prev.services.filter((item) => item.key !== row.key),
                    }))
                  }
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
          {!readOnly ? (
            <button
              type="button"
              className="ghost"
              onClick={() =>
                setForm((prev) => ({ ...prev, services: [...prev.services, emptyService()] }))
              }
            >
              Add another service
            </button>
          ) : null}
        </section>

        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>3</em> {t('health.diagnostics')}
            </h3>
            <span className={requiredReady.hasDiagnostics ? 'ok' : 'need'}>
              {requiredReady.hasDiagnostics ? t('health.ready') : t('health.required')}
            </span>
          </div>
          <p className="health-help">
            Tap common findings, then edit the note if you need more detail.
          </p>
          <ChipRow
            options={DIAGNOSTIC_SUGGESTIONS}
            disabled={readOnly}
            onPick={(option) =>
              setForm((prev) => ({
                ...prev,
                diagnosticFindings: appendSuggestion(prev.diagnosticFindings, option),
              }))
            }
          />
          <textarea
            rows={3}
            required
            disabled={readOnly}
            placeholder="Example: Check engine light on, mild misfire at idle…"
            value={form.diagnosticFindings}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, diagnosticFindings: e.target.value }))
            }
          />
        </section>

        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>4</em> {t('health.accident')}
            </h3>
            <span className={requiredReady.hasAccident ? 'ok' : 'need'}>
              {requiredReady.hasAccident ? t('health.ready') : t('health.required')}
            </span>
          </div>
          <p className="health-help">
            Record body or structural history. Use a suggestion if there is nothing to report.
          </p>
          <ChipRow
            options={ACCIDENT_SUGGESTIONS}
            disabled={readOnly}
            onPick={(option) =>
              setForm((prev) => ({
                ...prev,
                accidentRepairNotes: appendSuggestion(prev.accidentRepairNotes, option),
              }))
            }
          />
          <textarea
            rows={3}
            required
            disabled={readOnly}
            placeholder="Example: No accident history reported…"
            value={form.accidentRepairNotes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, accidentRepairNotes: e.target.value }))
            }
          />
        </section>

        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>5</em> {t('health.photos')}
            </h3>
            <span className={requiredReady.hasPhotos ? 'ok' : 'need'}>
              {requiredReady.hasPhotos ? t('health.ready') : t('health.required')}
            </span>
          </div>
          <p className="health-help">
            Add at least one clear photo (dash, tires, under-hood, or damage).
          </p>
          <div className="health-photo-grid">
            {form.photos.map((item) => (
              <div key={item.path} className="health-photo-wrap">
                <HealthPhoto item={item} />
                {!readOnly ? (
                  <button
                    type="button"
                    className="ghost danger"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        photos: prev.photos.filter((photo) => photo.path !== item.path),
                      }))
                    }
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {!readOnly ? (
            <label className="health-upload">
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={uploading}
                onChange={(e) => {
                  uploadPhotos(Array.from(e.target.files || []));
                  e.target.value = '';
                }}
              />
              {uploading ? 'Uploading…' : 'Add photos'}
            </label>
          ) : null}
        </section>

        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>6</em> Optional condition checks
            </h3>
            <span className="soft">Optional</span>
          </div>
          <div className="health-condition-block">
            <strong>Tires</strong>
            <ConditionPicker
              disabled={readOnly}
              value={form.tireCondition}
              onChange={(value) => setForm((prev) => ({ ...prev, tireCondition: value }))}
            />
          </div>
          <div className="health-condition-block">
            <strong>Battery</strong>
            <ConditionPicker
              disabled={readOnly}
              value={form.batteryCondition}
              onChange={(value) => setForm((prev) => ({ ...prev, batteryCondition: value }))}
            />
          </div>
          <label>
            Extra maintenance notes
            <textarea
              rows={2}
              disabled={readOnly}
              placeholder="Anything else from ownership history…"
              value={form.maintenanceHistoryNotes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, maintenanceHistoryNotes: e.target.value }))
              }
            />
          </label>
        </section>

        <section className="health-section">
          <div className="health-section-head">
            <h3>
              <em>7</em> {t('health.scores')}
            </h3>
            <span className="soft">Auto + override</span>
          </div>
          <p className="health-help">
            Scores update from your checklist. Drag a slider only if you need to override.
          </p>
          <div className={`health-overall health-band-${scoreBand(displayScores.overall_score)}`}>
            <div className="health-overall-head">
              <span>Overall</span>
              <strong>{displayScores.overall_score}</strong>
            </div>
            {!readOnly ? (
              <HealthScoreSlider
                ariaLabel="Overall score"
                value={displayScores.overall_score}
                onChange={(value) => setScore('overall_score', value)}
              />
            ) : null}
            {!readOnly && overrides.overall_score ? (
              <button type="button" className="ghost" onClick={() => resetScore('overall_score')}>
                Reset
              </button>
            ) : null}
          </div>
          <div className="health-score-grid">
            {SCORE_FIELDS.map((field) => (
              <div
                key={field}
                className={`health-score-card health-band-${scoreBand(displayScores[field])}`}
              >
                <div className="health-score-head">
                  <span>{SCORE_LABELS[field]}</span>
                  <strong>{displayScores[field]}</strong>
                </div>
                {!readOnly ? (
                  <>
                    <HealthScoreSlider
                      ariaLabel={`${SCORE_LABELS[field]} score`}
                      value={displayScores[field]}
                      onChange={(value) => setScore(field, value)}
                    />
                    {overrides[field] ? (
                      <button type="button" className="ghost" onClick={() => resetScore(field)}>
                        Reset
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        {!readOnly && !confirmFinalize ? (
          <div className="quote-toolbar">
            <button type="submit" className="ghost" disabled={saving || uploading}>
              Save draft
            </button>
            <button
              type="button"
              className="primary"
              disabled={saving || uploading || readyCount < 5}
              onClick={() => setConfirmFinalize(true)}
            >
              {t('health.finalize')}
            </button>
          </div>
        ) : null}
          </form>
        </>
      )}

      {confirmFinalize ? (
        <ConfirmBar
          title={t('health.finalizeTitle')}
          body="This updates the car’s mileage and sends a notification so the customer can review the scores in the app."
          confirmLabel={t('health.finalize')}
          busy={saving}
          onCancel={() => setConfirmFinalize(false)}
          onConfirm={finalizeReport}
        />
      ) : null}
    </article>
  );
}
